import React from 'react';
import { Link } from 'react-router-dom';
import useAuth from '../Authentication/components/hooks/useAuth';

export interface GalleryExperienceStatus {
    enabled: boolean;
    publishedRevision: number | null;
}

export function ClassicAppearanceNotice({ enabled }: { enabled?: boolean }) {
    if (!enabled) return null;
    return (
        <div className="my-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-gray-900">
            <strong>Classic appearance settings</strong>
            <p className="mt-1">
                These colors, backgrounds, and CTA settings do not style the
                custom gallery pages. Edit their appearance in the gallery
                designer. These fields remain editable for Classic pages and
                other surfaces, including sharing dialogs.
            </p>
            <p className="mt-1">
                Gallery Logo still applies to designer blocks set to Current
                gallery logo.
            </p>
        </div>
    );
}

export default function ExperienceEditorLink({
    activationId,
    galleryId,
    experience,
}: {
    activationId?: number | string;
    galleryId?: number | string;
    experience?: GalleryExperienceStatus | null;
}) {
    const { auth } = useAuth();
    const canEdit = auth?.roles?.some(
        (role: number) => role === 0 || role === 69,
    );
    const enabled = !!experience?.enabled;
    if (!activationId || !galleryId || (!canEdit && !enabled)) return null;
    return (
        <div className="my-4 rounded-lg border border-gray-200 bg-white p-4 text-gray-900">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <strong>
                        {enabled
                            ? 'Gallery designer is active'
                            : 'Classic gallery is active'}
                    </strong>
                    <p className="mt-1 text-sm text-gray-500">
                        {enabled
                            ? `Published revision ${experience?.publishedRevision} controls the Sharing, Activation gallery, and Recipient gallery pages. Change their layout, text, colors, backgrounds, and buttons in the designer.`
                            : 'The gallery settings below control the Classic gallery. Open the designer to create and enable a custom experience.'}
                    </p>
                    {enabled && (
                        <p className="mt-2 text-sm text-gray-700">
                            Passwords, gallery addresses, filtering, sharing
                            delivery, and data collection still apply here.
                            Classic appearance fields remain saved but do not
                            replace the published design.
                        </p>
                    )}
                </div>
                {canEdit && (
                    <Link
                        to={`/gallery-experiences/${activationId}/${galleryId}`}
                        className="rounded-md bg-black px-4 py-2 text-sm font-semibold text-white"
                    >
                        Open designer
                    </Link>
                )}
            </div>
        </div>
    );
}
